import type { GradedToken } from './grade'
import type { Island, SessionRecord } from '../types'
import { isDue } from './srs'

export type Readiness = 'cold' | 'warming' | 'ready' | 'automatic'

/**
 * Readiness, not progress.
 *
 * The question an island has to answer is "will this come out of my mouth
 * under pressure", so the ladder is gated on recent accuracy *and* repetition
 * across separate sessions. A single lucky run does not make an island
 * automatic, and a high score you got weeks ago does not either.
 */
export function readiness(island: Island): Readiness {
  const { performances, bestScore, lastScore } = island.stats
  if (performances === 0 || lastScore === null) return 'cold'
  if (island.srs.reps >= 4 && bestScore >= 95 && lastScore >= 90) return 'automatic'
  if (island.srs.reps >= 2 && lastScore >= 80) return 'ready'
  return 'warming'
}

export const READINESS_LABEL: Record<Readiness, string> = {
  cold: 'cold',
  warming: 'warming',
  ready: 'ready',
  automatic: 'automatic',
}

/** Per-tone tally for one graded attempt. Only pinyin drills produce these. */
export function toneTally(tokens: GradedToken[]): Record<number, { right: number; total: number }> {
  const out: Record<number, { right: number; total: number }> = {}
  for (const t of tokens) {
    if (t.status === 'extra') continue
    const tone = t.expectedTone
    const bucket = (out[tone] ??= { right: 0, total: 0 })
    bucket.total += 1
    if (t.status === 'correct') bucket.right += 1
  }
  return out
}

export interface ToneProfileRow {
  tone: number
  right: number
  total: number
  accuracy: number
}

/**
 * Which tone you keep dropping, across every pinyin drill you have run. This is
 * the long-run payoff of the ribbon: a single number per tone that tells you
 * where to aim practice.
 */
export function toneProfile(history: SessionRecord[]): ToneProfileRow[] {
  const totals = new Map<number, { right: number; total: number }>()

  for (const record of history) {
    if (!record.tones) continue
    for (const [key, value] of Object.entries(record.tones)) {
      const tone = Number(key)
      const bucket = totals.get(tone) ?? { right: 0, total: 0 }
      bucket.right += value.right
      bucket.total += value.total
      totals.set(tone, bucket)
    }
  }

  return [1, 2, 3, 4, 0]
    .map((tone) => {
      const bucket = totals.get(tone) ?? { right: 0, total: 0 }
      return {
        tone,
        right: bucket.right,
        total: bucket.total,
        accuracy: bucket.total === 0 ? 0 : Math.round((bucket.right / bucket.total) * 100),
      }
    })
    .filter((row) => row.total > 0)
}

export function dueIslands(islands: Island[], now = Date.now()): Island[] {
  return islands.filter((i) => isDue(i.srs, now))
}

/** Distinct calendar days with at least one drill, most recent first. */
export function practiceStreak(history: SessionRecord[], now = Date.now()): number {
  if (history.length === 0) return 0

  const days = new Set(history.map((r) => new Date(r.at).toDateString()))
  let streak = 0
  const cursor = new Date(now)

  // A streak survives today being empty — you have not broken it until you
  // miss a full day.
  if (!days.has(cursor.toDateString())) cursor.setDate(cursor.getDate() - 1)

  while (days.has(cursor.toDateString())) {
    streak += 1
    cursor.setDate(cursor.getDate() - 1)
  }
  return streak
}

export function scoreClass(score: number): string {
  if (score >= 85) return 'is-good'
  if (score >= 60) return 'is-mid'
  return 'is-poor'
}
